import cx from 'classnames'
import { ArtworkCard } from 'components/artwork/ArtworkCard'
import { Button } from 'components/common/Button'
import { Skeletons } from 'components/common/Skeletons'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import { FilterArtworkHitProps } from 'types'
import { pickHitPrices } from 'utils/instantsearch'
import { getSavedScroll, restoreScroll, saveScroll } from 'utils/scroll'
import {
  formatCurrency,
  getArtworkNameWithEdition,
  getLink,
} from 'utils/strings'
import { getArtworkUrl } from 'utils/urls'
const NoArtworksResults = dynamic(
  () =>
    import('components/common/NoArtworksResults').then(
      m => m.NoArtworksResults
    ),
  { ssr: false }
)

const Hit = (hit: FilterArtworkHitProps & { page?: number }) => {
  const { userCurrency } = useCurrencies()
  const { objectID, artists, name, images, editionName, page } = hit
  const { lowestAsk, highestBid } = pickHitPrices(hit, userCurrency)

  const id = Number(objectID.split('_')[1])

  let href = getArtworkUrl(artists, name, id, editionName)

  if (editionName === 'Main') {
    if (href.includes('?')) href += '&showRelevantEdition'
    else href += '?showRelevantEdition'
  }

  return (
    <ArtworkCard
      artists={artists}
      artworkName={getArtworkNameWithEdition(name, editionName)}
      artistName={artists?.[0]?.name}
      src={getLink(images?.thumbnail || images?.small || images?.xs)}
      href={href}
      onLinkClick={() => {
        saveScroll({ page, redirectedFrom: 'artworks' })
      }}
      highestBid={{ price: highestBid }}
      lowestAsk={{ price: lowestAsk }}
    />
  )
}

type Props = {
  showFilters: boolean
}

export const List = ({ showFilters }: Props) => {
  const router = useRouter()
  const { setIndexUiState, refresh } = useInstantSearch()
  const { hits, showMore, isLastPage, results } =
    useInfiniteHits<FilterArtworkHitProps>({
      showPrevious: false,
    })
  const [isLoading, setIsLoading] = useState(true)

  const { page: savedPage } = getSavedScroll()

  useEffect(() => {
    refresh()
  }, [refresh, router.pathname])

  useEffect(() => {
    if (!results || !showMore) {
      setIndexUiState(uiState => ({ ...uiState, page: 0 }))
      return
    }

    const { __isArtificial, page } = results

    while (page < savedPage) {
      showMore()
      return
    }

    if (__isArtificial) return

    restoreScroll('artworks').then(() => {
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMore, results])

  useEffect(() => {
    const { medium } = router.query

    if (!medium || typeof medium !== 'string') return

    setIndexUiState(prev => {
      return {
        ...prev,
        refinementList: {
          medium: [medium],
        },
      }
    })
  }, [router.query, setIndexUiState])

  const listClassName = cx(
    'grid w-full grid-cols-2 gap-y-16 gap-x-4 sm:grid-cols-3 xl:grid-cols-4',
    {
      '2xl:grid-cols-6 ': !showFilters,
      '2xl:grid-cols-5 ': showFilters,
    }
  )

  return (
    <div className='sticky top-20 w-full'>
      {(results?.__isArtificial || isLoading) && (
        <div className={listClassName}>
          <Skeletons type='artwork' count={60 * (savedPage + 1)} />
        </div>
      )}

      {!isLoading && (
        <ol className={listClassName}>
          {hits.map(hit => {
            return (
              <li key={hit.objectID}>
                <Hit {...hit} page={results?.page} />
              </li>
            )
          })}
        </ol>
      )}

      {!results?.__isArtificial && results?.nbHits === 0 && (
        <div className='w-full rounded sm:border sm:border-cool-300 sm:bg-cool-150 sm:px-6 sm:py-5 [&_h3]:!text-sm [&_h3]:sm:!text-base [&_h4]:!text-xs [&_h4]:sm:!text-sm'>
          <NoArtworksResults />
        </div>
      )}

      {!isLastPage && (
        <div className='mt-16 flex w-full flex-col items-center justify-center space-y-4'>
          <p className='text-sm text-cool-600'>
            You’ve viewed {hits.length} of{' '}
            {formatCurrency(results?.nbHits || 0, { style: 'decimal' })}{' '}
            artworks
          </p>

          <Button size='lg' style='solid-black' onClick={showMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}
